// IndexedDB-backed per-channel notification settings store.
//
// Ported from NotificationThreadSettingsStore.swift (iOS Stoat/E2EE).
//
// ## LWW semantics
//
// `set()` writes a record only when the incoming `ts` is strictly greater than
// the stored `ts`. Ties leave the existing record in place. This matches the
// iOS "strictly-newer-wins" rule.
//
// ## Lazy expired-mute revert
//
// When `get()` reads a record whose `mode === 'muted'` and `mutedUntil` has
// already passed (mutedUntil < now), it returns `'all'` — WITHOUT writing back
// to IndexedDB. The on-disk record remains `muted`. This is intentional: the
// revert is purely in-memory and ephemeral. Only an explicit `set()` call can
// overwrite the on-disk record. This mirrors iOS's lazy revert behaviour
// precisely.
//
// ## Raw read
//
// `getRaw()` returns the on-disk payload without applying the expiry logic.
// Used by tests that need to verify the disk record is still `muted` after a
// lazy-revert `get()`.
//
// Web storage: raw IndexedDB API (no idb-keyval) so the DB name can be
// configured for test isolation.

import type { NotificationDeliveryMode, NotificationThreadSettingsPayload } from "./NotificationThreadSettings";

const DEFAULT_DB_NAME = "kaether-notif-thread-settings";
const STORE_NAME = "settings";
const DB_VERSION = 1;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  const arr = new Uint8Array(hash);
  let hex = "";
  for (let i = 0; i < arr.length; i++) {
    hex += arr[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export class NotificationThreadSettingsStore {
  private readonly dbName: string;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  // -------------------------------------------------------------------------
  // Write

  /**
   * Persist settings for a channel, applying LWW.
   *
   * The record is written only when `payload.ts` is strictly greater than the
   * currently-stored `ts`. Ties are ignored (first writer wins among equals).
   *
   * Returns `true` if the record was written, `false` if it was rejected by
   * the LWW rule.
   */
  async set(
    channelId: string,
    payload: NotificationThreadSettingsPayload,
  ): Promise<boolean> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);

    return new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const getReq = store.get(key);

      getReq.onsuccess = () => {
        const existing = getReq.result as NotificationThreadSettingsPayload | undefined;
        // LWW: strictly later ts wins
        if (existing !== undefined && payload.ts <= existing.ts) {
          tx.abort();
          db.close();
          resolve(false);
          return;
        }
        store.put(payload, key);
      };

      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        if (tx.error?.name === "AbortError") {
          resolve(false);
        } else {
          reject(tx.error);
        }
      };
      tx.onabort = () => {
        db.close();
        resolve(false);
      };
    });
  }

  // -------------------------------------------------------------------------
  // Read (with lazy expired-mute revert)

  /**
   * Get the effective delivery mode for a channel.
   *
   * - Unknown channel → `'all'` (Signal default)
   * - Muted but `mutedUntil < now` → `'all'` (lazy revert; NO disk write)
   * - Otherwise → stored mode
   */
  async get(channelId: string, now: number = Date.now()): Promise<NotificationDeliveryMode> {
    const raw = await this.getRaw(channelId);
    if (raw === null) {
      return "all";
    }

    // Lazy expired-mute revert: if the mute has expired, return 'all' in
    // memory only. Do NOT write back to IndexedDB.
    if (
      raw.mode === "muted" &&
      raw.mutedUntil !== undefined &&
      raw.mutedUntil < now
    ) {
      return "all";
    }

    return raw.mode;
  }

  // -------------------------------------------------------------------------
  // Raw read (bypasses expiry logic)

  /**
   * Read the on-disk payload verbatim, without applying the expired-mute
   * revert. Returns `null` for unknown channels.
   *
   * Used by tests that need to verify the disk record remains `muted` after a
   * lazy-revert call to `get()`.
   */
  async getRaw(channelId: string): Promise<NotificationThreadSettingsPayload | null> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);

    return new Promise<NotificationThreadSettingsPayload | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(key);
      tx.oncomplete = () => {
        db.close();
        resolve((req.result as NotificationThreadSettingsPayload) ?? null);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  // -------------------------------------------------------------------------
  // Cleanup

  /**
   * Remove the record for a single channel (convenience for testing / resets).
   */
  async clear(channelId: string): Promise<void> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.objectStore(STORE_NAME).delete(key);
    });
  }

  /**
   * Remove all records from this store.
   */
  async clearAll(): Promise<void> {
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.objectStore(STORE_NAME).clear();
    });
  }
}
